import type { Pagination as PageMeta, SiteSummary, UserWithContext } from "@taskiq/shared";
import { useApi } from "./useApi";

/**
 * The two lists most screens need about the scope they are working in.
 *
 * They exist as hooks rather than as inline `useApi` calls because **the key is
 * the cache identity**: two components asking the same question with URLs that
 * differ by a parameter neither of them cares about produce two requests and
 * two answers that can disagree. The scope switcher and the dashboard both want
 * "the sites in this company"; the schedule, the appointment panel and the
 * dashboard's crew all want "the active people at this site". One spelling
 * each, and SWR does the rest.
 *
 * The Flutter app has had `companySitesProvider` and `siteUsersProvider` since
 * it was built. This is the console catching up, not a new idea.
 */

interface SiteList {
  sites: (SiteSummary & { company_id: string })[];
  pagination: PageMeta;
}

interface UserList {
  users: UserWithContext[];
  pagination: PageMeta;
}

/**
 * A hundred rather than paging: this feeds pickers that need the whole set, not
 * the first screenful. Past a hundred sites in one company the pickers need
 * server-side search rather than a bigger number here.
 */
const FULL_LIST = 100;

/** Every site in one company. Null `companyId` means "unbounded", not "none". */
export function useCompanySites(companyId: string | null) {
  return useApi<SiteList>(
    `/sites?pageSize=${FULL_LIST}${companyId ? `&company_id=${companyId}` : ""}`
  );
}

/**
 * The active people at one site.
 *
 * Needs `user.read`, which a field officer lacks - gate the call site on the
 * capability and pass `null` rather than firing a request that will 403.
 */
export function useSiteUsers(siteId: string | null) {
  return useApi<UserList>(
    siteId ? `/sites/${siteId}/users?pageSize=${FULL_LIST}&status=active` : null
  );
}
